import React, {useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { Button, Form, Input, Typography, message, theme } from "antd";
import {ExclamationCircleOutlined, LockOutlined, MailOutlined} from "@ant-design/icons";
import {useGetProfileMutation, useLoginMutation} from "../services/nestjs-base-api";
import {FormattedMessage} from "react-intl";
import {useIntl} from "react-intl";
import AuthLayout from "../components/AuthLayout";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

const { useToken } = theme;
const { Text, Link } = Typography;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 24px;
  }
  
  .ant-input-affix-wrapper,
  .ant-input {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
      box-shadow: 0 0 0 2px rgba(65, 105, 225, 0.1);
    }
  }
  
  .ant-input-prefix {
    color: #4169e1;
    font-size: 18px;
    margin-right: 12px;
  }
`;

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
  }
  
  &:disabled {
    background: #f5f5f5;
    box-shadow: none;
    transform: none;
  }
`;

const FooterWrapper = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: #4169e1;
  font-weight: 500;
  
  &:hover {
    color: #1890ff;
  }
`;

const SigninPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { login } = useAuth();
    const { token } = useToken();
    const navigate = useNavigate();
    const [apiLogin, apiLoginResult] = useLoginMutation();
    const [getProfile, apiGetProfileResult] = useGetProfileMutation();
    const [messageApi, contextHolder] = message.useMessage();

    const onLoginError = useCallback(() => {
        messageApi.open({
            type: 'error',
            content: <FormattedMessage id='signIn.failed' />,
            className: 'custom-class',
            style: {
                marginTop: '1vh',
                fontSize: '1.2rem'
            },
            duration: 4,
            icon: <ExclamationCircleOutlined style={{fontSize: '1.2rem', paddingBottom: '0.1rem'}} />
        });
    }, [messageApi]);

    const handleLogin = useCallback(async (values: any) => {
        const authResponse = await apiLogin({email: values.email, password: values.password});

        if ('data' in authResponse) {
            const profileResponse = await getProfile({jwtToken: authResponse.data.access_token});

            if ('data' in profileResponse) {
                login({...profileResponse.data, jwtToken: authResponse.data.access_token});
            }
        } else {
            onLoginError();
        }
    }, [apiLogin, getProfile, login, onLoginError]);

    return (
        <>
            {contextHolder}
            <AuthLayout 
                title={intl.formatMessage({ id: 'signIn.title' })}
                subtitle={intl.formatMessage({ id: 'signIn.welcome' })}
            >
                <StyledForm
                    name="normal_login"
                    onFinish={handleLogin}
                    layout="vertical"
                    requiredMark="optional"
                >
                    <Form.Item
                        name="email"
                        rules={[
                            {
                                type: "email",
                                required: true,
                                message: intl.formatMessage({ id: 'signIn.form.email.missing' }),
                            },
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined/>}
                            placeholder={intl.formatMessage({ id: 'signIn.form.email' })}
                        />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: intl.formatMessage({ id: 'signIn.form.password.missing' }),
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined/>}
                            type="password"
                            placeholder={intl.formatMessage({ id: 'signIn.form.password' })}
                        />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: '16px' }}>
                        <div style={{ textAlign: 'right' }}>
                            <StyledLink onClick={() => navigate('/forgot')}>
                                <FormattedMessage id='signIn.forgot' />
                            </StyledLink>
                        </div>
                    </Form.Item>
                    <Form.Item>
                        <StyledButton 
                            block={true} 
                            type="primary" 
                            htmlType="submit" 
                            disabled={apiLoginResult.isLoading || apiGetProfileResult.isLoading}
                            loading={apiLoginResult.isLoading || apiGetProfileResult.isLoading}
                        >
                            <FormattedMessage id='signIn.form.submit' />
                        </StyledButton>
                        <FooterWrapper>
                            <Text style={{ color: token.colorTextSecondary }}>
                                <FormattedMessage id='signIn.noAccount' />
                            </Text>{" "}
                            <StyledLink onClick={() => navigate('/sign-up')}>
                                <FormattedMessage id='signIn.signUp' />
                            </StyledLink>
                        </FooterWrapper>
                    </Form.Item>
                </StyledForm>
            </AuthLayout>
        </>
    );
}

export default SigninPage;
